import { useEffect, useRef, useState, useContext, useCallback } from 'react';
import { UserContext } from '@context/user/UserContext';
import { Rol } from '@src/types/common';

interface UseAutoRefreshOptions {
  userId: string;
  countryCode: string;
  onRefresh: () => Promise<void>;
  intervalMs?: number;
  isActive?: boolean; // Controla si el hook debe estar activo (detiene completamente el intervalo si es false)
}

interface UseAutoRefreshReturn {
  isRefreshing: boolean;
  startRefresh: () => void;
  stopRefresh: () => void;
}

/**
 * Hook para auto-refresh cada 30 segundos solo para usuarios auditor (rol 9)
 * Recarga el grid completo usando el método existente getInspections
 */
export const useAutoRefresh = ({
  userId,
  countryCode,
  onRefresh,
  intervalMs = 30000, // 30 segundos por defecto
  isActive = true, // Por defecto está activo si no se especifica
}: UseAutoRefreshOptions): UseAutoRefreshReturn => {
  const { state } = useContext(UserContext);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(true);
  const onRefreshRef = useRef(onRefresh);

  // Mantener la referencia del callback actualizada
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const startRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(async () => {
      // Validar condiciones antes de hacer la petición
      const currentShouldRefresh =
        state?.currentUser?.rolId === Rol.AUDIT && isActiveRef.current;

      if (!currentShouldRefresh) {
        return;
      }

      try {
        setIsRefreshing(true);
        await onRefreshRef.current();
      } catch (error) {
        console.error('❌ Error en auto-refresh:', error);
      } finally {
        setIsRefreshing(false);
      }
    }, intervalMs);
  }, [intervalMs, state?.currentUser?.rolId]);

  const stopRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsRefreshing(false);
  };

  // Manejar visibilidad de la página
  useEffect(() => {
    if (!isActive) return; // No hacer nada si el hook no está activo

    const handleVisibilityChange = () => {
      isActiveRef.current = !document.hidden;

      const isAuditor = state?.currentUser?.rolId === Rol.AUDIT;
      const hasRequiredData = !!userId && !!countryCode;

      if (isAuditor && hasRequiredData) {
        if (document.hidden) {
          stopRefresh();
        } else {
          startRefresh();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state?.currentUser?.rolId, userId, countryCode, isActive, startRefresh]);

  // Iniciar/parar refresh según condiciones
  useEffect(() => {
    // Si isActive es false, detener completamente el intervalo
    if (!isActive) {
      stopRefresh();
      return;
    }

    const isAuditor = state?.currentUser?.rolId === Rol.AUDIT;
    const hasRequiredData = !!userId && !!countryCode;

    if (isAuditor && hasRequiredData) {
      startRefresh();
    } else {
      stopRefresh();
    }

    return () => {
      stopRefresh();
    };
  }, [state?.currentUser?.rolId, userId, countryCode, isActive, startRefresh]);

  return {
    isRefreshing,
    startRefresh,
    stopRefresh,
  };
};
